import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { ArrowLeft, TrendingUp } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export const TrendsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const api = useApi(token);
  const [patient, setPatient] = useState(null);
  const [labResults, setLabResults] = useState([]);
  const [selectedTest, setSelectedTest] = useState("");
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      const patientData = await api.get(`/patients/${patientId}`);
      setPatient(patientData);
      const results = await api.get(`/files/${patientId}/lab-results`);
      setLabResults(results);
      
      if (results.length > 0 && results[0].lab_values.length > 0) {
        setSelectedTest(results[0].lab_values[0].test_code);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
    }
  };

  useEffect(() => {
    if (selectedTest && labResults.length > 0) {
      const data = labResults
        .map(result => {
          const testValue = result.lab_values.find(v => v.test_code === selectedTest);
          if (testValue && testValue.value !== null) {
            return {
              date: new Date(result.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
              value: testValue.value,
              refLow: testValue.ref_low,
              refHigh: testValue.ref_high,
              unit: testValue.unit
            };
          }
          return null;
        })
        .filter(d => d !== null)
        .reverse();
      
      setChartData(data);
    }
  }, [selectedTest, labResults]);

  const allTests = labResults.length > 0 ? 
    [...new Map(labResults.flatMap(r => r.lab_values).map(v => [v.test_code, v])).values()] : [];

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fcfbf9" }}>
        <p style={{ color: "#555555", fontFamily: "Poppins, sans-serif" }}>Loading trends...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#fcfbf9", fontFamily: "Poppins, sans-serif" }}>
      <header className="border-b" style={{ backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)", borderColor: "#e5e5e5" }}>
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            onClick={() => navigate(`/patient/${patientId}`)} 
            variant="ghost" 
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold" style={{ color: "#652D90" }}>
            <TrendingUp size={24} className="inline mr-2" />
            Trends Analysis - {patient.full_name}
          </h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {labResults.length < 2 ? (
          <Card className="rounded-2xl border-[#e5e5e5]">
            <CardContent className="py-12 text-center">
              <p style={{ color: "#555555" }}>Need at least 2 lab results to show trends. Upload more reports to see trends.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="rounded-2xl border-[#e5e5e5] mb-6">
              <CardHeader>
                <CardTitle style={{ color: "#652D90" }}>Select Test to Visualize</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {allTests.map((test) => (
                    <Button
                      key={test.test_code}
                      onClick={() => setSelectedTest(test.test_code)}
                      variant={selectedTest === test.test_code ? "default" : "outline"}
                      className="rounded-full"
                      style={selectedTest === test.test_code ? { backgroundColor: "#652D90" } : {}}
                    >
                      {test.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {chartData.length > 0 && (
              <Card className="rounded-2xl border-[#e5e5e5]">
                <CardHeader>
                  <CardTitle style={{ color: "#333333" }}>
                    {allTests.find(t => t.test_code === selectedTest)?.name} Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="date" style={{ fontFamily: "Poppins, sans-serif", fontSize: 12 }} />
                      <YAxis style={{ fontFamily: "Poppins, sans-serif", fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: "#fcfbf9", 
                          border: "1px solid #e5e5e5", 
                          borderRadius: "8px",
                          fontFamily: "Poppins, sans-serif"
                        }} 
                      />
                      <Legend wrapperStyle={{ fontFamily: "Poppins, sans-serif" }} />
                      {chartData[0].refLow !== null && (
                        <Line type="monotone" dataKey="refLow" stroke="#1fb25a" strokeDasharray="5 5" name="Reference Low" />
                      )}
                      {chartData[0].refHigh !== null && (
                        <Line type="monotone" dataKey="refHigh" stroke="#e90d01" strokeDasharray="5 5" name="Reference High" />
                      )}
                      <Line type="monotone" dataKey="value" stroke="#652D90" strokeWidth={3} name={`Value (${chartData[0].unit})`} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};
